import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Badge from "@mui/material/Badge";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";

import { useAuth } from "AuthContext";
import { API_URL } from "config";
import { hasPermission, mergeGrantedPermissions } from "utils/granularPermissions";
import { ProjectCommunicationTimelineDialog } from "../projects/components/ProjectCommunicationDialog";
import ProjectLiveChatBadge from "layouts/project-live-chat/ProjectLiveChatBadge";
import { useProjectLiveChat } from "../project-live-chat/useProjectLiveChat";
import CompleteDeviceMetric from "../projects/CompleteDeviceMetric";

const emptyStats = {
  complete: 0,
  total: 0,
  terminate: 0,
  quota_full: 0,
  security_terminate: 0,
  initiate: 0,
};

const emptyBreakdown = {
  "1h": { complete: 0, clicks: 0, v_rtr: null },
  "12h": { complete: 0, clicks: 0, v_rtr: null },
  "24h": { complete: 0, clicks: 0, v_rtr: null },
  overall: { complete: 0, clicks: 0, v_rtr: null },
};

const metricPeriods = [
  { key: "1h", label: "1" },
  { key: "12h", label: "12" },
  { key: "24h", label: "24" },
  { key: "overall", label: "Overall" },
];

const ALL_SCOPE_VALUE = "__all__";
const VENDOR_PROJECT_REALTIME_DEBOUNCE_MS = 750;

const vendorProjectTabs = [
  { value: "live", label: "Live" },
  { value: "pause", label: "Pause" },
  { value: "invoiced", label: "Invoiced" },
  { value: "invoice_at_zero", label: "Invoice at Zero" },
  { value: "closed", label: "Closed" },
];

const vendorProjectTabValues = vendorProjectTabs.map((tab) => tab.value);

const tabStyles = {
  live: { color: "#0ea5e9", background: "#e0f2fe", border: "#7dd3fc" },
  pause: { color: "#f6a800", background: "#fff7df", border: "#ffd666" },
  invoiced: { color: "#2563eb", background: "#dbeafe", border: "#93c5fd" },
  invoice_at_zero: { color: "#475569", background: "#f1f5f9", border: "#cbd5e1" },
  closed: { color: "#ef4444", background: "#fee2e2", border: "#fecaca" },
};

const liveStatuses = ["soft_launch", "full_launch", "hard_launch", "live"];

const tabFromStatus = (status) => {
  const normalized = String(status || "live").toLowerCase();
  if (liveStatuses.includes(normalized)) return "live";
  return vendorProjectTabValues.includes(normalized) ? normalized : "live";
};

const tableHeaderCellSx = {
  backgroundColor: "#f8f9fa",
  color: "#8392ab",
  fontSize: "12px",
  fontWeight: 400,
  letterSpacing: 0,
  textTransform: "capitalize",
  borderBottom: "1px solid #e9ecef",
  py: 0.75,
  px: 0.35,
  whiteSpace: "normal",
  lineHeight: 1.15,
  verticalAlign: "middle",
};

const tableBodyCellSx = {
  color: "#334155",
  fontSize: "12px",
  fontWeight: 400,
  borderBottom: "1px solid #edf2f7",
  py: 0.4,
  px: 0.35,
  verticalAlign: "middle",
  lineHeight: 1.15,
  overflowWrap: "anywhere",
};

const metricHeaderCellSx = {
  ...tableHeaderCellSx,
  px: 0.15,
  py: 0.55,
  textAlign: "center",
  borderLeft: "1px solid #e9ecef",
};

const metricBodyCellSx = {
  ...tableBodyCellSx,
  px: 0.1,
  py: 0.35,
  textAlign: "center",
  whiteSpace: "nowrap",
  borderLeft: "1px solid #edf2f7",
};

const statsMetricCellSx = {
  ...metricBodyCellSx,
  width: 60,
  minWidth: 60,
  fontWeight: 700,
};

const valueOrDash = (value) => {
  if (value === null || typeof value === "undefined" || value === "") return "-";
  return value;
};

const truncatedText = (value, limit = 20) => {
  const text = String(value || "");
  return text.length > limit ? text.slice(0, limit) : text;
};

const htmlToPlainText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const countryText = (row) => {
  const country = row?.country_name || row?.country?.name || "";
  const code = row?.country_code || row?.country?.code || "";

  if (country && code) return `${country} (${code})`;
  return country || code;
};

const withQueryParam = (url, key, value) => {
  if (!url) return "";
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

const linkUrlsForRow = (row) => {
  const liveUrl = row?.live_url || row?.copy_url || "";
  const testUrl = row?.test_url || (liveUrl ? withQueryParam(liveUrl, "test", "1") : "");

  return { liveUrl, testUrl };
};

const numberOrDash = (value, prefix = "") => {
  if (value === null || typeof value === "undefined" || value === "") return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return `${prefix}${numeric}`;
};

const numericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const roundedMetricText = (value) => {
  const number = numericValue(value);
  if (number === null) return "-";
  const whole = Math.floor(number);
  return number - whole > 0.5 ? whole + 1 : whole;
};

const formatIrDisplay = (value) => roundedMetricText(value);

const loiMetricColor = (average, configured) => {
  const averageValue = numericValue(average);
  const configuredValue = numericValue(configured);

  if (averageValue === null || configuredValue === null) return "#334155";
  return Math.abs(averageValue - configuredValue) <= 5 ? "#15803d" : "#dc2626";
};

const irMetricColor = (average, configured) => {
  const actualValue = numericValue(average);
  const configuredValue = numericValue(configured);

  if (actualValue === null || configuredValue === null) return "#334155";
  return Math.abs(actualValue - configuredValue) <= 10 ? "#15803d" : "#dc2626";
};

const quotaText = (value) => {
  const quota = Number(value || 0);
  return Number.isFinite(quota) && quota > 0 ? quota : "QS";
};

const statusText = (row) => {
  if (row?.status_label) return row.status_label;
  const normalized = String(row?.status || row?.project_status || "").toLowerCase();
  const labels = {
    soft_launch: "Soft Launch",
    full_launch: "Full Launch",
    hard_launch: "Hard Launch",
    live: "Live",
    pause: "Pause",
    invoiced: "Invoiced",
    invoice_at_zero: "Invoice at Zero",
    closed: "Closed",
  };

  return labels[normalized] || valueOrDash(normalized);
};

function IrMetric({ average, configured }) {
  return (
    <SoftBox component="span" sx={{ color: "#334155", fontWeight: 400 }}>
      <SoftBox
        component="span"
        sx={{
          color: irMetricColor(average, configured),
          fontWeight: numericValue(average) === null ? 400 : 700,
        }}
      >
        {formatIrDisplay(average)}
      </SoftBox>
      /{formatIrDisplay(configured)}
    </SoftBox>
  );
}

IrMetric.propTypes = {
  average: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  configured: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function LoiMetric({ average, configured }) {
  return (
    <SoftBox component="span" sx={{ color: "#334155", fontWeight: 400 }}>
      <SoftBox
        component="span"
        sx={{
          color: loiMetricColor(average, configured),
          fontWeight: numericValue(average) === null ? 400 : 700,
        }}
      >
        {roundedMetricText(average)}
      </SoftBox>
      /{roundedMetricText(configured)}
    </SoftBox>
  );
}

LoiMetric.propTypes = {
  average: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  configured: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function MetricHoverCard({ label, breakdown = emptyBreakdown }) {
  return (
    <SoftBox
      sx={{
        minWidth: 320,
        p: 1,
        color: "#1e293b",
        backgroundColor: "#fde047",
        borderRadius: "6px",
      }}
    >
      <SoftTypography variant="caption" fontWeight="regular" color="dark" sx={{ fontSize: "12px" }}>
        {label}
      </SoftTypography>
      <SoftBox
        sx={{
          display: "grid",
          gridTemplateColumns: "78px repeat(4, minmax(44px, 1fr))",
          mt: 0.75,
          borderTop: "1px solid rgba(15, 23, 42, 0.2)",
          borderLeft: "1px solid rgba(15, 23, 42, 0.2)",
        }}
      >
        {["", ...metricPeriods.map((period) => period.label)].map((heading, index) => (
          <SoftBox
            key={`heading-${heading || index}`}
            sx={{
              px: 0.5,
              py: 0.45,
              textAlign: index === 0 ? "left" : "center",
              borderRight: "1px solid rgba(15, 23, 42, 0.2)",
              borderBottom: "1px solid rgba(15, 23, 42, 0.2)",
              fontSize: "12px",
              fontWeight: 400,
            }}
          >
            {heading}
          </SoftBox>
        ))}
        {[
          { label: "Complete", key: "complete" },
          { label: "Clicks", key: "clicks" },
          { label: "V.RTR", key: "v_rtr" },
        ].flatMap((metricRow) => [
          <SoftBox
            key={`${metricRow.key}-label`}
            sx={{
              px: 0.5,
              py: 0.45,
              borderRight: "1px solid rgba(15, 23, 42, 0.2)",
              borderBottom: "1px solid rgba(15, 23, 42, 0.2)",
              fontSize: "12px",
              fontWeight: 400,
            }}
          >
            {metricRow.label}
          </SoftBox>,
          ...metricPeriods.map((period) => (
            <SoftBox
              key={`${metricRow.key}-${period.key}`}
              sx={{
                px: 0.5,
                py: 0.45,
                textAlign: "center",
                borderRight: "1px solid rgba(15, 23, 42, 0.2)",
                borderBottom: "1px solid rgba(15, 23, 42, 0.2)",
                fontSize: "10px",
                fontWeight: 400,
              }}
            >
              {breakdown?.[period.key]?.[metricRow.key] ?? "-"}
            </SoftBox>
          )),
        ])}
      </SoftBox>
    </SoftBox>
  );
}

MetricHoverCard.propTypes = {
  label: PropTypes.string.isRequired,
  breakdown: PropTypes.objectOf(
    PropTypes.shape({
      complete: PropTypes.number,
      terminate: PropTypes.number,
      quota_full: PropTypes.number,
      security_terminate: PropTypes.number,
      initiate: PropTypes.number,
    })
  ),
};

function VendorProjects() {
  const { isAuthReady, permissions = [], userPermission = [], user } = useAuth();
  const { canUseProjectLiveChat, openProjectLiveChat } = useProjectLiveChat();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [pagination, setPagination] = useState({ from: 0, to: 0, total: 0, last_page: 1 });
  const [tabCounts, setTabCounts] = useState({});
  const [loadedCommunicationTarget, setLoadedCommunicationTarget] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, color: "success", message: "" });
  const [linkDialog, setLinkDialog] = useState(null);
  const [timelineDialog, setTimelineDialog] = useState(null);
  const [actorContext, setActorContext] = useState({
    actorType: null,
    vendorType: null,
    canManageWorkforce: false,
  });
  const [workforceDialog, setWorkforceDialog] = useState(null);
  const [workforceLoading, setWorkforceLoading] = useState(false);
  const [workforceSaving, setWorkforceSaving] = useState(false);
  const [workforceBranch, setWorkforceBranch] = useState(null);
  const [workforceSubBranch, setWorkforceSubBranch] = useState([]);
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const activeFetchControllerRef = useRef(null);

  useEffect(() => {
    setPageInput(String(currentPage || 1));
  }, [currentPage]);

  const activeTab = useMemo(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    return vendorProjectTabValues.includes(tab) ? tab : "live";
  }, [location.search]);

  const communicationTarget = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const countryWinId = params.get("country_win_id") || "";
    const requestedType = params.get("communication");

    return {
      countryWinId,
      type: ["mail", "comment", "chat"].includes(requestedType) ? requestedType : "",
    };
  }, [location.search]);

  const grantedPermissions = useMemo(
    () => mergeGrantedPermissions(permissions, userPermission),
    [permissions, userPermission]
  );

  const canViewPage = hasPermission(grantedPermissions, "vendor_projects.pages.list.view");
  const canSearch = hasPermission(grantedPermissions, "vendor_projects.actions.search");
  const canComment = hasPermission(grantedPermissions, "vendor_projects.actions.comment");
  const canMail = hasPermission(grantedPermissions, "vendor_projects.actions.mail");
  const canChat = hasPermission(grantedPermissions, "vendor_projects.actions.chat");
  const canViewSurveyResponses = hasPermission(
    grantedPermissions,
    "vendor_projects.actions.view_survey_responses"
  );
  const canViewAllSurveyResponses =
    canViewSurveyResponses &&
    hasPermission(grantedPermissions, "vendor_projects.survey_responses.pages.all.view");
  const canViewProjectSurveyResponses =
    canViewSurveyResponses &&
    hasPermission(grantedPermissions, "vendor_projects.survey_responses.pages.project.view");
  const canViewCountrySurveyResponses =
    canViewSurveyResponses &&
    hasPermission(grantedPermissions, "vendor_projects.survey_responses.pages.country.view");
  const canExpandDetailsAction = hasPermission(
    grantedPermissions,
    "vendor_projects.actions.expand_details"
  );
  const canViewCopyLinkColumn = hasPermission(
    grantedPermissions,
    "vendor_projects.fields.copy_link.view"
  );
  const canViewInternalLiveLink = hasPermission(
    grantedPermissions,
    "vendor_projects.actions.view_internal_live_link"
  );
  const canCopyInternalLiveLink = hasPermission(
    grantedPermissions,
    "vendor_projects.actions.copy_internal_live_link"
  );
  const canViewInternalTestLink = hasPermission(
    grantedPermissions,
    "vendor_projects.actions.view_internal_test_link"
  );
  const canCopyInternalTestLink = hasPermission(
    grantedPermissions,
    "vendor_projects.actions.copy_internal_test_link"
  );
  const canViewExternalLiveLink = hasPermission(
    grantedPermissions,
    "vendor_projects.actions.view_external_live_link"
  );
  const canCopyExternalLiveLink = hasPermission(
    grantedPermissions,
    "vendor_projects.actions.copy_external_live_link"
  );
  const canViewExternalTestLink = hasPermission(
    grantedPermissions,
    "vendor_projects.actions.view_external_test_link"
  );
  const canCopyExternalTestLink = hasPermission(
    grantedPermissions,
    "vendor_projects.actions.copy_external_test_link"
  );
  const canAssignWorkforcePermission = hasPermission(
    grantedPermissions,
    "vendor_projects.actions.assign_workforce_scope"
  );
  const canViewTab = useCallback(
    (tab) => hasPermission(grantedPermissions, `vendor_projects.tabs.${tab}.view`),
    [grantedPermissions]
  );
  const canManageWorkforce =
    canAssignWorkforcePermission &&
    actorContext.actorType === "vendor_contact" &&
    actorContext.vendorType === "internal" &&
    actorContext.canManageWorkforce;

  const canField = useCallback(
    (field) => hasPermission(grantedPermissions, `vendor_projects.fields.${field}.view`),
    [grantedPermissions]
  );

  const canViewProjectCode = canField("project_code");
  const canViewProjectName = canField("project_name");
  const canViewProjectManager = canField("project_manager");
  const canViewVendor = canField("vendor");
  const canViewCountry = canField("country");
  const canViewTargeting = canField("targeting");
  const canViewIr = canField("ir");
  const canViewLoi = canField("loi");
  const canViewStudyType = canField("study_type");
  const canViewProjectType = canField("project_type");
  const canViewCpi = canField("vendor_cpi");
  const canViewStats = canField("stats");
  const canViewCompleteDeviceBrowser = canField("complete_device_browser_counting");
  const canViewStatus = canField("status");
  const canViewComments = canComment;
  const canViewAnyLink =
    canViewInternalLiveLink ||
    canViewInternalTestLink ||
    canViewExternalLiveLink ||
    canViewExternalTestLink;
  const canViewLink = activeTab === "live" && canViewCopyLinkColumn && canViewAnyLink;
  const canExpandDetails = canExpandDetailsAction && canViewTargeting;
  const visibleTabs = useMemo(
    () => vendorProjectTabs.filter((tab) => canViewTab(tab.value)),
    [canViewTab]
  );
  const showRowActions =
    canUseProjectLiveChat ||
    canManageWorkforce ||
    canViewComments ||
    canMail ||
    canChat ||
    canExpandDetails;

  const linkAccessForRow = (row) => {
    const source = String(row?.link_source || row?.vendor_type || "external").toLowerCase();
    const isInternal = source === "internal";

    return {
      source: isInternal ? "internal" : "external",
      canViewLive: isInternal ? canViewInternalLiveLink : canViewExternalLiveLink,
      canCopyLive: isInternal ? canCopyInternalLiveLink : canCopyExternalLiveLink,
      canViewTest: isInternal ? canViewInternalTestLink : canViewExternalTestLink,
      canCopyTest: isInternal ? canCopyInternalTestLink : canCopyExternalTestLink,
    };
  };

  const workforceBranchOptions = useMemo(() => {
    const options = workforceDialog?.branchOptions || [];
    return options.length > 1 ? [{ value: ALL_SCOPE_VALUE, label: "All Branches" }, ...options] : options;
  }, [workforceDialog]);

  const workforceSubBranchOptions = useMemo(() => {
    const options = workforceDialog?.subBranchOptions || [];
    if (!workforceBranch) return [];

    const filtered =
      workforceBranch.value === ALL_SCOPE_VALUE
        ? options
        : options.filter((option) => String(option.branchId) === String(workforceBranch.value));

    return filtered.length > 1
      ? [{ value: ALL_SCOPE_VALUE, label: "All Sub Branches", branchId: String(workforceBranch.value) }, ...filtered]
      : filtered;
  }, [workforceBranch, workforceDialog]);

  const tableColumnCount =
    [
      canViewProjectCode,
      canViewProjectName,
      canViewProjectManager,
      canViewVendor,
      canViewCountry,
      canViewIr,
      canViewLoi,
      canViewStudyType,
      canViewProjectType,
      canViewCpi,
      canViewStatus,
      showRowActions,
      canViewLink,
    ].filter(Boolean).length +
    (canViewCpi ? 1 : 0) +
    (canViewStats ? 5 : 0) +
    (canViewCompleteDeviceBrowser ? 1 : 0);

  const showMessage = useCallback((message, color = "success") => {
    setSnackbar({ open: true, color, message });
  }, []);

  const openSurveyResponses = useCallback(
    (row = null, scope = "all") => {
      const canOpenScope =
        scope === "country"
          ? canViewCountrySurveyResponses
          : scope === "project"
          ? canViewProjectSurveyResponses
          : canViewAllSurveyResponses;
      if (!canOpenScope) return;

      const params = new URLSearchParams();
      params.set("tab", activeTab);

      if (scope === "country" && row?.project_id && row?.id) {
        navigate({
          pathname: `/vendor-projects/${row.project_id}/country-wins/${row.id}/survey-responses`,
          search: `?${params.toString()}`,
        });
        return;
      }

      if (scope === "project" && row?.project_id) {
        navigate({
          pathname: `/vendor-projects/${row.project_id}/survey-responses`,
          search: `?${params.toString()}`,
        });
        return;
      }

      navigate({
        pathname: "/vendor-projects/survey-responses",
        search: `?${params.toString()}`,
      });
    },
    [
      activeTab,
      canViewAllSurveyResponses,
      canViewCountrySurveyResponses,
      canViewProjectSurveyResponses,
      navigate,
    ]
  );

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
      Accept: "application/json",
    }),
    []
  );

  const copyText = useCallback(
    async (value, successMessage = "Link copied.") => {
      if (!value) {
        showMessage("Link is not available.", "error");
        return;
      }

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = value;
          textarea.setAttribute("readonly", "");
          textarea.style.position = "fixed";
          textarea.style.left = "-9999px";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        showMessage(successMessage);
      } catch (error) {
        showMessage("Failed to copy link.", "error");
      }
    },
    [showMessage]
  );

  const fetchRows = useCallback(async ({ silent = false, skipIfBusy = false } = {}) => {
    if (!canViewPage) return;
    if (skipIfBusy && activeFetchControllerRef.current) return;

    activeFetchControllerRef.current?.abort();
    const controller = new AbortController();
    activeFetchControllerRef.current = controller;

    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(currentPage));
      params.set("per_page", "50");
      if (communicationTarget.countryWinId) {
        params.set("country_win_id", communicationTarget.countryWinId);
      } else {
        params.set("status", activeTab);
        if (canSearch && appliedSearch) {
          params.set("search", appliedSearch);
        }
      }

      const response = await fetch(`${API_URL}/vendor-projects?${params.toString()}`, {
        headers: authHeaders,
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === 0) {
        throw new Error(data?.message || "Failed to load vendor projects.");
      }

      setActorContext({
        actorType: data?.actor_type || null,
        vendorType: data?.vendor_type || null,
        canManageWorkforce: Boolean(data?.can_manage_workforce_scope),
      });
      setTabCounts(data?.vendor_project_counts || {});
      const page = data?.projects || {};
      setRows(Array.isArray(page?.data) ? page.data : Array.isArray(page) ? page : []);
      setPagination({
        from: Number(page?.from || 0),
        to: Number(page?.to || 0),
        total: Number(page?.total || 0),
        last_page: Number(page?.last_page || 1),
      });
    } catch (error) {
      if (error.name === "AbortError") return;

      if (!silent) {
        setRows([]);
        setTabCounts({});
        setPagination({ from: 0, to: 0, total: 0, last_page: 1 });
        showMessage(error.message || "Failed to load vendor projects.", "error");
      }
    } finally {
      if (activeFetchControllerRef.current === controller) {
        activeFetchControllerRef.current = null;
        if (!silent) {
          setLoading(false);
          setLoadedCommunicationTarget(communicationTarget.countryWinId);
        }
      }
    }
  }, [
    authHeaders,
    activeTab,
    canSearch,
    canViewPage,
    communicationTarget.countryWinId,
    currentPage,
    appliedSearch,
    showMessage,
  ]);

  useEffect(() => {
    if (isAuthReady) fetchRows();
  }, [fetchRows, isAuthReady]);

  useEffect(() => {
    if (!isAuthReady || visibleTabs.length === 0) return;
    if (visibleTabs.some((tab) => tab.value === activeTab)) return;

    const params = new URLSearchParams(location.search);
    params.set("tab", visibleTabs[0].value);
    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      { replace: true }
    );
  }, [activeTab, isAuthReady, location.pathname, location.search, navigate, visibleTabs]);

  useEffect(() => {
    if (!isAuthReady || !canViewPage) return undefined;

    const refreshWhenVisible = () => {
      if (document.visibilityState !== "visible") return;
      fetchRows({ silent: true, skipIfBusy: true });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshWhenVisible();
    };

    window.addEventListener("focus", refreshWhenVisible);
    window.addEventListener("realtime-connected", refreshWhenVisible);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      window.removeEventListener("realtime-connected", refreshWhenVisible);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [canViewPage, fetchRows, isAuthReady]);

  useEffect(() => {
    if (!isAuthReady || !canViewPage) return undefined;
    let refreshTimeoutId;

    const scheduleRealtimeRefresh = () => {
      window.clearTimeout(refreshTimeoutId);
      refreshTimeoutId = window.setTimeout(() => {
        if (document.visibilityState === "visible") {
          fetchRows({ silent: true, skipIfBusy: true });
        }
      }, VENDOR_PROJECT_REALTIME_DEBOUNCE_MS);
    };

    const refreshCommunicationBadges = (event) => {
      const moduleName = event.detail?.meta?.module || event.detail?.module;
      if (moduleName === "vendor_projects") {
        scheduleRealtimeRefresh();
      }
    };

    const refreshVendorProjects = () => scheduleRealtimeRefresh();

    window.addEventListener("echo-event", refreshCommunicationBadges);
    window.addEventListener("vendor-projects-realtime", refreshVendorProjects);

    return () => {
      window.clearTimeout(refreshTimeoutId);
      window.removeEventListener("echo-event", refreshCommunicationBadges);
      window.removeEventListener("vendor-projects-realtime", refreshVendorProjects);
    };
  }, [canViewPage, fetchRows, isAuthReady]);

  useEffect(
    () => () => {
      const controller = activeFetchControllerRef.current;
      activeFetchControllerRef.current = null;
      controller?.abort();
    },
    []
  );

  const handleLinkAction = (row) => {
    if (activeTab !== "live") return;

    const { liveUrl, testUrl } = linkUrlsForRow(row);
    const access = linkAccessForRow(row);
    const hasLiveAccess = access.canViewLive && liveUrl;
    const hasTestAccess = access.canViewTest && testUrl;

    if (hasLiveAccess || hasTestAccess) {
      setLinkDialog({
        ...row,
        linkAccess: access,
        live_url: hasLiveAccess ? liveUrl : "",
        test_url: hasTestAccess ? testUrl : "",
      });
      return;
    }
  };

  const openTimelineDialog = (type, row) => {
    setTimelineDialog({ type, row });
  };

  const openWorkforceDialog = async (row) => {
    if (!canManageWorkforce || !row?.project_id) return;

    setWorkforceDialog({
      row,
      branchOptions: [],
      subBranchOptions: [],
      automatic: false,
      requiresAssignment: false,
      hideRuleCount: 0,
    });
    setWorkforceBranch(null);
    setWorkforceSubBranch([]);
    setWorkforceLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/vendor-projects/projects/${row.project_id}/workforce-assignment`,
        { headers: authHeaders }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === 0) {
        throw new Error(data?.message || "Failed to load project workforce assignment.");
      }

      const branchOptions = (data?.branches || []).map((branch) => ({
        value: String(branch.id),
        label: branch.name,
      }));
      const subBranchOptions = (data?.sub_branches || []).map((subBranch) => ({
        value: String(subBranch.id),
        label: subBranch.name,
        branchId: String(subBranch.branch_id),
      }));
      const assignments = Array.isArray(data?.assignments)
        ? data.assignments
        : data?.assignment
          ? [data.assignment]
          : [];

      const uniqueValues = (values) => [...new Set(values.filter(Boolean).map((value) => String(value)))];
      const assignedBranchIds = uniqueValues(assignments.map((assignment) => assignment.branch_id));
      const assignedSubBranchIds = uniqueValues(assignments.map((assignment) => assignment.subbranch_id));

      const selectedBranch =
        assignedBranchIds.length > 1
          ? { value: ALL_SCOPE_VALUE, label: "All Branches" }
          : branchOptions.find((option) => String(option.value) === String(assignedBranchIds[0] || "")) ||
            (branchOptions.length === 1 ? branchOptions[0] : null);
      const subBranchesForSelectedBranch =
        selectedBranch?.value === ALL_SCOPE_VALUE
          ? subBranchOptions
          : subBranchOptions.filter(
              (option) => String(option.branchId) === String(selectedBranch?.value || "")
            );
      const selectedSubBranch =
        assignedSubBranchIds.length > 0
          ? subBranchOptions.filter((option) =>
              assignedSubBranchIds.includes(String(option.value))
            )
          : subBranchesForSelectedBranch.length === 1
            ? [subBranchesForSelectedBranch[0]]
            : [];

      setWorkforceDialog({
        row,
        project: data?.project,
        branchOptions,
        subBranchOptions,
        automatic: Boolean(data?.automatic),
        requiresAssignment: Boolean(data?.requires_assignment),
        hideRuleCount: assignments.length,
      });
      setWorkforceBranch(selectedBranch);
      setWorkforceSubBranch(selectedSubBranch);
    } catch (error) {
      setWorkforceDialog(null);
      showMessage(error.message || "Failed to load project workforce assignment.", "error");
    } finally {
      setWorkforceLoading(false);
    }
  };

  const saveWorkforceAssignment = async () => {
    const selectedSubBranchValues = Array.isArray(workforceSubBranch)
      ? workforceSubBranch
      : workforceSubBranch
        ? [workforceSubBranch]
        : [];

    if (!workforceDialog?.row?.project_id || !workforceBranch || selectedSubBranchValues.length === 0) return;

    const branchOptions = workforceDialog?.branchOptions || [];
    const subBranchOptions = workforceDialog?.subBranchOptions || [];
    const selectedBranches =
      workforceBranch.value === ALL_SCOPE_VALUE ? branchOptions : [workforceBranch];
    const hasAllSubBranches = selectedSubBranchValues.some(
      (option) => option?.value === ALL_SCOPE_VALUE
    );
    const assignmentsByKey = new Map();

    selectedBranches.forEach((branch) => {
      const selectedSubBranches =
        hasAllSubBranches
          ? subBranchOptions.filter((option) => String(option.branchId) === String(branch.value))
          : selectedSubBranchValues.filter(
              (option) => String(option.branchId) === String(branch.value)
            );

      selectedSubBranches.forEach((subBranch) => {
        const key = `${branch.value}:${subBranch.value}`;
        assignmentsByKey.set(key, {
          branch_id: Number(branch.value),
          subbranch_id: Number(subBranch.value),
        });
      });
    });

    const assignments = Array.from(assignmentsByKey.values());
    if (assignments.length === 0) {
      showMessage("Please select at least one branch and sub branch.", "error");
      return;
    }

    setWorkforceSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/vendor-projects/projects/${workforceDialog.row.project_id}/workforce-assignment`,
        {
          method: "PUT",
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            assignments,
          }),
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === 0) {
        throw new Error(data?.message || "Failed to save project hide rules.");
      }

      setWorkforceDialog(null);
      showMessage(data?.message || "Project workforce hide rules saved successfully.");
      fetchRows({ silent: true });
    } catch (error) {
      showMessage(error.message || "Failed to save project hide rules.", "error");
    } finally {
      setWorkforceSaving(false);
    }
  };

  const clearWorkforceAssignment = async () => {
    if (!workforceDialog?.row?.project_id || workforceSaving) return;

    setWorkforceSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/vendor-projects/projects/${workforceDialog.row.project_id}/workforce-assignment`,
        {
          method: "PUT",
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ assignments: [] }),
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === 0) {
        throw new Error(data?.message || "Failed to clear project hide rules.");
      }

      setWorkforceDialog(null);
      showMessage(data?.message || "Project workforce hide rules cleared successfully.");
      fetchRows({ silent: true });
    } catch (error) {
      showMessage(error.message || "Failed to clear project hide rules.", "error");
    } finally {
      setWorkforceSaving(false);
    }
  };

  const toggleRowDetails = (rowId) => {
    setExpandedRowIds((current) =>
      current.includes(rowId) ? current.filter((id) => id !== rowId) : [...current, rowId]
    );
  };

  const markCommunicationRead = useCallback((type, rowId) => {
    if (!rowId) return;

    setRows((currentRows) => {
      let changed = false;
      const nextRows = currentRows.map((row) => {
        if (row.id !== rowId) return row;

        const counts = row.communication_counts || {};
        const currentTypeCounts = counts[type] || { total: 0, unread: 0 };
        if (Number(currentTypeCounts.unread || 0) <= 0) return row;

        changed = true;

        return {
          ...row,
          communication_counts: {
            ...counts,
            [type]: {
              ...currentTypeCounts,
              unread: 0,
            },
          },
        };
      });

      return changed ? nextRows : currentRows;
    });
  }, []);

  useEffect(() => {
    if (
      loadedCommunicationTarget !== communicationTarget.countryWinId ||
      !communicationTarget.countryWinId ||
      !communicationTarget.type ||
      timelineDialog
    ) {
      return;
    }

    const targetRow = rows.find(
      (row) => String(row.id) === String(communicationTarget.countryWinId)
    );
    const params = new URLSearchParams(location.search);
    params.delete("country_win_id");
    params.delete("communication");
    const nextSearch = params.toString();

    if (targetRow) {
      setTimelineDialog({ type: communicationTarget.type, row: targetRow });
    } else {
      showMessage("The linked vendor project is not available for this account.", "error");
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [
    communicationTarget.countryWinId,
    communicationTarget.type,
    loadedCommunicationTarget,
    location.pathname,
    location.search,
    navigate,
    rows,
    showMessage,
    timelineDialog,
  ]);

  const renderStatus = (row) => {
    const label = statusText(row);
    const tab = tabFromStatus(row?.status || row?.project_status);
    const style = tabStyles[tab] || tabStyles.live;

    return (
      <SoftBox
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          px: 0.8,
          py: 0.2,
          borderRadius: "999px",
          fontSize: "12px",
          lineHeight: 1,
          color: style.color,
          border: `1px solid ${style.border}`,
          backgroundColor: style.background,
        }}
      >
        {label}
      </SoftBox>
    );
  };

  const handleTabChange = (event, nextTab) => {
    if (nextTab === activeTab) return;

    setLoading(true);
    setCurrentPage(1);
    const params = new URLSearchParams(location.search);
    params.set("tab", nextTab);
    params.delete("country_win_id");
    params.delete("communication");
    navigate({
      pathname: location.pathname,
      search: `?${params.toString()}`,
    });
  };

  const openVendorProjectPage = (value) => {
    const page = Math.max(1, Math.min(pagination.last_page || 1, Number(value) || 1));
    setPageInput(String(page));
    setCurrentPage(page);
  };

  if (isAuthReady && !canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={2}>
          <Card sx={{ p: 2 }}>
            <SoftTypography variant="h6">You do not have permission to view vendor projects.</SoftTypography>
          </Card>
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={0.3}>
        <Card sx={{ px: 1.5, py: 0.225 }}>
          <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.1875}>
            <SoftBox>
              <SoftTypography variant="h5">Vendor Projects</SoftTypography>
              <SoftTypography variant="caption" color="text">
                {user?.vendor_type === "internal"
                  ? "Internal"
                  : user?.vendor_type === "external"
                  ? "External"
                  : "Assigned"}{" "}
                project links
              </SoftTypography>
            </SoftBox>
            <SoftBox display="flex" alignItems="center" justifyContent="flex-end" gap={0.75} flexWrap="wrap">
              {canViewAllSurveyResponses && (
                <SoftButton
                  color="info"
                  size="small"
                  onClick={() => openSurveyResponses()}
                  sx={{
                    minHeight: 30,
                    height: 30,
                    width: 150,
                    minWidth: 150,
                    px: 1.3,
                    py: 0,
                    fontSize: "10px",
                    textTransform: "none",
                  }}
                >
                  <Icon sx={{ mr: 0.5, fontSize: "14px !important" }}>table_view</Icon>
                  Survey Responses
                </SoftButton>
              )}
              {canSearch && (
                <SoftBox width={{ xs: "100%", sm: "150px" }}>
                  <SoftInput
                    size="small"
                    placeholder="Search..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        const nextSearch = search.trim();
                        if (nextSearch === appliedSearch && currentPage === 1) {
                          fetchRows();
                        } else {
                          setAppliedSearch(nextSearch);
                          if (currentPage !== 1) setCurrentPage(1);
                        }
                      }
                    }}
                    sx={{
                      width: "100%",
                      height: 30,
                      minHeight: 30,
                      fontSize: "10px",
                      "& .MuiInputBase-input": {
                        height: 30,
                        boxSizing: "border-box",
                        py: 0,
                        px: 1,
                      },
                    }}
                  />
                </SoftBox>
              )}
            </SoftBox>
          </SoftBox>

          {visibleTabs.length > 0 && (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 34,
                mb: 0,
                backgroundColor: "transparent",
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTab-root": {
                  minHeight: 32,
                  height: 32,
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  mx: 0.25,
                  px: 1.5,
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "none",
                },
              }}
            >
              {visibleTabs.map((tab) => {
                const tabStyle = tabStyles[tab.value] || tabStyles.live;
                return (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    label={`${tab.label} (${Number(tabCounts[tab.value] || 0)})`}
                    sx={{
                      color: `${tabStyle.color} !important`,
                      backgroundColor: `${tabStyle.background} !important`,
                      borderColor: tabStyle.border,
                      "&.Mui-selected": {
                        color: "#fff !important",
                        backgroundColor: `${tabStyle.color} !important`,
                        borderColor: tabStyle.color,
                      },
                    }}
                  />
                );
              })}
            </Tabs>
          )}

          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            py={0.25}
          >
            <SoftTypography variant="caption" color="text">
              Showing {pagination.from} to {pagination.to} of {pagination.total} entries
            </SoftTypography>
            {pagination.last_page > 1 && (
              <SoftBox display="flex" alignItems="center">
                <SoftPagination
                  item
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage <= 1 || loading}
                  sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>first_page</Icon>
                </SoftPagination>
                <SoftPagination
                  item
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage <= 1 || loading}
                  sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>chevron_left</Icon>
                </SoftPagination>
                <SoftBox display="flex" alignItems="center" gap={0.4} mx={0.4}>
                  <SoftBox
                    component="input"
                    type="text"
                    value={pageInput}
                    onChange={(event) => setPageInput(event.target.value.replace(/\D/g, ""))}
                    onBlur={() => openVendorProjectPage(pageInput)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") openVendorProjectPage(pageInput);
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    aria-label="Page number"
                    sx={{
                      width: 42,
                      minWidth: 42,
                      maxWidth: 42,
                      height: 22,
                      fontSize: "10px",
                      fontFamily: "inherit",
                      color: "#344767",
                      lineHeight: "20px",
                      textAlign: "center",
                      boxSizing: "border-box",
                      p: 0,
                      border: "1px solid #d2d6da",
                      borderRadius: "8px",
                      outline: "none",
                      "&:focus": {
                        borderColor: "#17c1e8",
                        boxShadow: "0 0 0 2px rgba(23, 193, 232, 0.18)",
                      },
                    }}
                  />
                  <SoftTypography sx={{ fontSize: "10px", color: "#64748b" }}>
                    / {pagination.last_page}
                  </SoftTypography>
                </SoftBox>
                <SoftPagination
                  item
                  onClick={() =>
                    setCurrentPage((page) => Math.min(pagination.last_page, page + 1))
                  }
                  disabled={currentPage >= pagination.last_page || loading}
                  sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>chevron_right</Icon>
                </SoftPagination>
                <SoftPagination
                  item
                  onClick={() => setCurrentPage(pagination.last_page)}
                  disabled={currentPage >= pagination.last_page || loading}
                  sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>last_page</Icon>
                </SoftPagination>
              </SoftBox>
            )}
          </SoftBox>

          <TableContainer
            sx={{
              overflowX: "auto",
              boxShadow: "none",
              borderTop: "1px solid #edf2f7",
            }}
          >
            <Table size="small" sx={{ minWidth: 980 }}>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  {canViewProjectCode && (
                    <TableCell rowSpan={2} sx={tableHeaderCellSx}>
                      Project Code
                    </TableCell>
                  )}
                  {canViewProjectName && (
                    <TableCell rowSpan={2} sx={tableHeaderCellSx}>
                      Project Name
                    </TableCell>
                  )}
                  {canViewProjectManager && (
                    <TableCell rowSpan={2} sx={tableHeaderCellSx}>
                      Project Manager
                    </TableCell>
                  )}
                  {canViewVendor && (
                    <TableCell rowSpan={2} sx={tableHeaderCellSx}>
                      Vendor
                    </TableCell>
                  )}
                  {canViewCountry && (
                    <TableCell rowSpan={2} sx={metricHeaderCellSx}>
                      Country
                    </TableCell>
                  )}
                  {canViewIr && (
                    <TableCell rowSpan={2} sx={metricHeaderCellSx}>
                      IR(%)
                    </TableCell>
                  )}
                  {canViewLoi && (
                    <TableCell rowSpan={2} sx={metricHeaderCellSx}>
                      LOI(mins)
                    </TableCell>
                  )}
                  {canViewStudyType && (
                    <TableCell rowSpan={2} sx={metricHeaderCellSx}>
                      Study Type
                    </TableCell>
                  )}
                  {canViewProjectType && (
                    <TableCell rowSpan={2} sx={metricHeaderCellSx}>
                      Project Type
                    </TableCell>
                  )}
                  {canViewCpi && (
                    <TableCell rowSpan={2} sx={metricHeaderCellSx}>
                      Vendor CPI
                    </TableCell>
                  )}
                  {canViewCpi && (
                    <TableCell rowSpan={2} sx={metricHeaderCellSx}>
                      Vendor Revenue
                    </TableCell>
                  )}
                  {canViewCompleteDeviceBrowser && (
                    <TableCell rowSpan={2} sx={{ ...metricHeaderCellSx, width: 140, minWidth: 140 }}>
                      Device/Browser
                    </TableCell>
                  )}
                  {canViewStats && (
                    <TableCell
                      colSpan={5}
                      sx={{
                        ...metricHeaderCellSx,
                        width: 300,
                        minWidth: 300,
                        backgroundColor: "#fde047",
                        color: "#1e293b",
                      }}
                    >
                      Stats
                    </TableCell>
                  )}
                  {canViewStatus && (
                    <TableCell rowSpan={2} sx={metricHeaderCellSx}>
                      Status
                    </TableCell>
                  )}
                  {showRowActions && (
                    <TableCell rowSpan={2} sx={metricHeaderCellSx}>
                      <SoftBox display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                        {canManageWorkforce && (
                          <Icon sx={{ fontSize: "0.85rem !important" }}>account_tree</Icon>
                        )}
                        {canViewComments && <Icon sx={{ fontSize: "0.85rem !important" }}>comment</Icon>}
                        {canMail && <Icon sx={{ fontSize: "0.85rem !important" }}>mail</Icon>}
                        {canChat && <Icon sx={{ fontSize: "0.85rem !important" }}>chat</Icon>}
                        {canUseProjectLiveChat && <Icon sx={{ fontSize: "0.85rem !important" }}>forum</Icon>}
                        {canExpandDetails && (
                          <Icon sx={{ fontSize: "0.9rem !important" }}>keyboard_arrow_down</Icon>
                        )}
                      </SoftBox>
                    </TableCell>
                  )}
                  {canViewLink && (
                    <TableCell rowSpan={2} sx={metricHeaderCellSx}>
                      Copy Link
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  {canViewStats &&
                    [
                      { label: "c/QS", color: "#0ea5e9" },
                      { label: "T", color: "#ef4444" },
                      { label: "OQ", color: "#f59e0b" },
                      { label: "ST", color: "#ef4444" },
                      { label: "P", color: "#334155" },
                    ].map((column) => (
                      <TableCell
                        key={`stats-${column.label}`}
                        sx={{
                          ...metricHeaderCellSx,
                          width: 60,
                          minWidth: 60,
                          color: column.color,
                          backgroundColor: "#f8fafc",
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(tableColumnCount, 1)}
                      align="center"
                      sx={{ ...tableBodyCellSx, py: 5 }}
                    >
                      <SoftBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        role="status"
                        aria-live="polite"
                      >
                        <CircularProgress color="info" size={32} thickness={4} />
                        <SoftTypography variant="caption" color="text">
                          Loading {vendorProjectTabs.find((tab) => tab.value === activeTab)?.label || "vendor"}{" "}
                          projects...
                        </SoftTypography>
                      </SoftBox>
                    </TableCell>
                  </TableRow>
                )}

                {!loading && rows.map((row) => {
                  const stats = row?.project_metrics?.stats || emptyStats;
                  const breakdown = row?.project_metrics?.breakdown || emptyBreakdown;
                  const { liveUrl, testUrl } = linkUrlsForRow(row);
                  const rowLinkAccess = linkAccessForRow(row);
                  const liveLinkAvailable = rowLinkAccess.canViewLive && Boolean(liveUrl);
                  const testLinkAvailable = rowLinkAccess.canViewTest && Boolean(testUrl);
                  const linkDisabled = activeTab !== "live" || (!liveLinkAvailable && !testLinkAvailable);
                  const isExpanded = expandedRowIds.includes(row.id);
                  const targetingText = htmlToPlainText(row.targeting);
                  const completeColor = Number(stats.complete || 0) > 0 ? "#16a34a" : "#dc2626";

                  return (
                    <Fragment key={row.id}>
                      <TableRow hover sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                        {canViewProjectCode && (
                          <TableCell sx={tableBodyCellSx}>
                            {canViewProjectSurveyResponses && row.project_id ? (
                              <Tooltip title="View project survey responses">
                                <SoftBox
                                  component="button"
                                  type="button"
                                  onClick={() => openSurveyResponses(row, "project")}
                                  sx={{
                                    border: 0,
                                    background: "transparent",
                                    p: 0,
                                    m: 0,
                                    color: "#0ea5e9",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    lineHeight: 1.15,
                                    textAlign: "left",
                                    fontFamily: "inherit",
                                  }}
                                >
                                  {valueOrDash(row.project_code)}
                                </SoftBox>
                              </Tooltip>
                            ) : (
                              valueOrDash(row.project_code)
                            )}
                          </TableCell>
                        )}
                        {canViewProjectName && (
                          <TableCell sx={tableBodyCellSx}>
                            <Tooltip title={row.project_name || ""}>
                              <span>{valueOrDash(truncatedText(row.project_name))}</span>
                            </Tooltip>
                          </TableCell>
                        )}
                        {canViewProjectManager && (
                          <TableCell sx={tableBodyCellSx}>
                            <Tooltip title={row.project_manager_name || ""}>
                              <span>{valueOrDash(row.project_manager_name)}</span>
                            </Tooltip>
                          </TableCell>
                        )}
                        {canViewVendor && (
                          <TableCell sx={tableBodyCellSx}>
                            <Tooltip title={row.vendor_name || ""}>
                              <span>{valueOrDash(row.vendor_name)}</span>
                            </Tooltip>
                          </TableCell>
                        )}
                        {canViewCountry && (
                          <TableCell sx={metricBodyCellSx}>
                            {canViewCountrySurveyResponses && row.project_id && row.id ? (
                              <Tooltip title="View country link survey responses">
                                <SoftBox
                                  component="button"
                                  type="button"
                                  onClick={() => openSurveyResponses(row, "country")}
                                  sx={{
                                    border: 0,
                                    background: "transparent",
                                    p: 0,
                                    m: 0,
                                    color: "#0ea5e9",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    lineHeight: 1.15,
                                    textAlign: "center",
                                    fontFamily: "inherit",
                                  }}
                                >
                                  {valueOrDash(countryText(row))}
                                </SoftBox>
                              </Tooltip>
                            ) : (
                              valueOrDash(countryText(row))
                            )}
                          </TableCell>
                        )}
                        {canViewIr && (
                          <TableCell sx={metricBodyCellSx}>
                            <IrMetric average={row?.project_metrics?.average_ir} configured={row.ir} />
                          </TableCell>
                        )}
                        {canViewLoi && (
                          <TableCell sx={metricBodyCellSx}>
                            <LoiMetric average={row?.project_metrics?.average_loi} configured={row.loi} />
                          </TableCell>
                        )}
                        {canViewStudyType && (
                          <TableCell sx={metricBodyCellSx}>{valueOrDash(row.study_type)}</TableCell>
                        )}
                        {canViewProjectType && (
                          <TableCell sx={metricBodyCellSx}>{valueOrDash(row.project_type)}</TableCell>
                        )}
                        {canViewCpi && (
                          <TableCell sx={metricBodyCellSx}>{numberOrDash(row.cpi, "$")}</TableCell>
                        )}
                        {canViewCpi && (
                          <TableCell sx={metricBodyCellSx}>
                            {numberOrDash(row.vendor_revenue, "$")}
                          </TableCell>
                        )}
                        {canViewCompleteDeviceBrowser && (
                          <TableCell sx={{ ...metricBodyCellSx, width: 140, minWidth: 140, px: 0.35 }}>
                            <CompleteDeviceMetric
                              metric={row.project_metrics?.complete_device_metrics}
                              total={stats.complete}
                            />
                          </TableCell>
                        )}
                        {canViewStats && (
                          <>
                            <TableCell
                              sx={{
                                ...statsMetricCellSx,
                                color: completeColor,
                              }}
                            >
                              <Tooltip
                                placement="bottom"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      p: 0,
                                      maxWidth: "none",
                                      backgroundColor: "transparent",
                                      boxShadow: "none",
                                    },
                                  },
                                }}
                                title={<MetricHoverCard label={row.project_name || row.project_code} breakdown={breakdown} />}
                              >
                                <SoftBox
                                  component="span"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                    minHeight: 17,
                                    cursor: "help",
                                  }}
                                >
                                  <SoftBox component="span" sx={{ color: completeColor }}>
                                    {Number(stats.complete || 0)}
                                  </SoftBox>
                                  <SoftBox component="span" sx={{ color: "#0ea5e9" }}>
                                    /{quotaText(row.vendor_quota)}
                                  </SoftBox>
                                </SoftBox>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ ...statsMetricCellSx, color: "#dc2626" }}>
                              {Number(stats.terminate || 0)}
                            </TableCell>
                            <TableCell sx={{ ...statsMetricCellSx, color: "#d97706" }}>
                              {Number(stats.quota_full || 0)}
                            </TableCell>
                            <TableCell sx={{ ...statsMetricCellSx, color: "#dc2626" }}>
                              {Number(stats.security_terminate || 0)}
                            </TableCell>
                            <TableCell sx={statsMetricCellSx}>{Number(stats.initiate || 0)}</TableCell>
                          </>
                        )}
                        {canViewStatus && <TableCell sx={metricBodyCellSx}>{renderStatus(row)}</TableCell>}
                        {showRowActions && (
                          <TableCell sx={metricBodyCellSx}>
                            <SoftBox display="flex" justifyContent="center" alignItems="center" gap={0.15}>
                              {canManageWorkforce && (
                                <Tooltip
                                  title={
                                    row.workforce_assignment_summary ||
                                    (row.workforce_assignment
                                      ? `Hidden from ${row.workforce_assignment.branch_name} / ${row.workforce_assignment.subbranch_name}`
                                      : "Hide project from branch and sub branch")
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    color={
                                      row.workforce_assignments?.length ? "warning" : "success"
                                    }
                                    sx={{ p: 0.2 }}
                                    onClick={() => openWorkforceDialog(row)}
                                  >
                                    <Icon sx={{ fontSize: "0.85rem !important" }}>
                                      account_tree
                                    </Icon>
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canViewComments && (
                                <Tooltip title="Comments">
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    sx={{ p: 0.2 }}
                                    onClick={() => openTimelineDialog("comment", row)}
                                  >
                                    <Badge
                                      color="error"
                                      badgeContent={Number(row.communication_counts?.comment?.unread || 0)}
                                      invisible={Number(row.communication_counts?.comment?.unread || 0) <= 0}
                                      sx={{ "& .MuiBadge-badge": { minWidth: 14, height: 14, fontSize: "0.55rem" } }}
                                    >
                                      <Icon sx={{ fontSize: "0.85rem !important" }}>comment</Icon>
                                    </Badge>
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canMail && (
                                <Tooltip title="Mail">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    sx={{ p: 0.2 }}
                                    onClick={() => openTimelineDialog("mail", row)}
                                  >
                                    <Badge
                                      color="error"
                                      badgeContent={Number(row.communication_counts?.mail?.unread || 0)}
                                      invisible={Number(row.communication_counts?.mail?.unread || 0) <= 0}
                                      sx={{ "& .MuiBadge-badge": { minWidth: 14, height: 14, fontSize: "0.55rem" } }}
                                    >
                                      <Icon sx={{ fontSize: "0.85rem !important" }}>mail</Icon>
                                    </Badge>
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canChat && (
                                <Tooltip title="Chat">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    sx={{ p: 0.2 }}
                                    onClick={() => openTimelineDialog("chat", row)}
                                  >
                                    <Badge
                                      color="error"
                                      badgeContent={Number(row.communication_counts?.chat?.unread || 0)}
                                      invisible={Number(row.communication_counts?.chat?.unread || 0) <= 0}
                                      sx={{ "& .MuiBadge-badge": { minWidth: 14, height: 14, fontSize: "0.55rem" } }}
                                    >
                                      <Icon sx={{ fontSize: "0.85rem !important" }}>chat</Icon>
                                    </Badge>
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canUseProjectLiveChat && row.project_id && (
                                <Tooltip title="Project live chat">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    sx={{ p: 0.2 }}
                                    onClick={() =>
                                      openProjectLiveChat({
                                        id: row.project_id,
                                        quest_code: row.project_code || row.quest_code || row.project_id,
                                        project_title: row.project_name || row.project_title || "",
                                      })
                                    }
                                  >
                                    <ProjectLiveChatBadge projectId={row.project_id}>
                                      <Icon sx={{ fontSize: "0.85rem !important" }}>forum</Icon>
                                    </ProjectLiveChatBadge>
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canExpandDetails && (
                                <Tooltip title={isExpanded ? "Hide details" : "View details"}>
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => toggleRowDetails(row.id)}
                                    sx={{ p: 0.2 }}
                                  >
                                    <Icon sx={{ fontSize: "0.9rem !important" }}>
                                      {isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                                    </Icon>
                                  </IconButton>
                                </Tooltip>
                              )}
                            </SoftBox>
                          </TableCell>
                        )}
                        {canViewLink && (
                          <TableCell sx={metricBodyCellSx}>
                            <Tooltip
                              title={
                                liveLinkAvailable && testLinkAvailable
                                  ? "Show live and test links"
                                  : liveLinkAvailable
                                  ? "Show live link"
                                  : "Show test link"
                              }
                            >
                              <span>
                                <SoftButton
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  disabled={linkDisabled}
                                  onClick={() => handleLinkAction(row)}
                                  sx={{
                                    minWidth: 0,
                                    height: 20,
                                    minHeight: "20px !important",
                                    px: 0.55,
                                    py: 0,
                                    fontSize: "9px",
                                    lineHeight: 1,
                                    whiteSpace: "nowrap",
                                    textTransform: "none",
                                  }}
                                >
                                  {liveLinkAvailable && testLinkAvailable
                                    ? "Live/Test"
                                    : liveLinkAvailable
                                    ? "Live"
                                    : "Test"}
                                </SoftButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                      {canExpandDetails && isExpanded && (
                        <TableRow>
                          <TableCell colSpan={Math.max(tableColumnCount, 1)} sx={{ ...tableBodyCellSx, backgroundColor: "#f8fafc", p: 1 }}>
                            <SoftBox>
                              <SoftTypography variant="caption" fontWeight="bold" color="dark">
                                Targeting
                              </SoftTypography>
                              <SoftTypography variant="caption" color="text" display="block" sx={{ mt: 0.25, whiteSpace: "normal" }}>
                                {targetingText || "No Targeting"}
                              </SoftTypography>
                            </SoftBox>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}

                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={Math.max(tableColumnCount, 1)} align="center" sx={tableBodyCellSx}>
                      No vendor projects found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </SoftBox>

      <ProjectCommunicationTimelineDialog
        open={Boolean(timelineDialog)}
        type={timelineDialog?.type}
        row={timelineDialog?.row}
        onClose={() => setTimelineDialog(null)}
        onRead={markCommunicationRead}
        onNotify={showMessage}
      />

      <Dialog open={Boolean(linkDialog)} onClose={() => setLinkDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Vendor Project Link</DialogTitle>
        <DialogContent>
          {linkDialog?.linkAccess?.canViewLive && linkDialog?.live_url && (
            <SoftBox mb={1}>
              <SoftTypography variant="caption" fontWeight="bold">
                Live Link
              </SoftTypography>
              <SoftInput value={linkDialog.live_url} disabled />
            </SoftBox>
          )}
          {linkDialog?.linkAccess?.canViewTest && linkDialog?.test_url && (
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold">
                Test Link
              </SoftTypography>
              <SoftInput value={linkDialog.test_url} disabled />
            </SoftBox>
          )}
        </DialogContent>
        <DialogActions>
          <SoftButton color="secondary" variant="outlined" onClick={() => setLinkDialog(null)}>
            Close
          </SoftButton>
          {linkDialog?.linkAccess?.canViewLive && linkDialog?.linkAccess?.canCopyLive && linkDialog?.live_url && (
            <SoftButton color="info" onClick={() => copyText(linkDialog.live_url, "Live link copied.")}>
              Copy Live
            </SoftButton>
          )}
          {linkDialog?.linkAccess?.canViewTest && linkDialog?.linkAccess?.canCopyTest && linkDialog?.test_url && (
            <SoftButton color="info" variant="outlined" onClick={() => copyText(linkDialog.test_url, "Test link copied.")}>
              Copy Test
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(workforceDialog)}
        onClose={() => {
          if (!workforceSaving) setWorkforceDialog(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Hide Project From Workforce</DialogTitle>
        <DialogContent>
          <SoftTypography variant="caption" color="text" display="block" mb={1.5}>
            Project Code:{" "}
            <SoftBox component="span" fontWeight="bold">
              {workforceDialog?.project?.project_code ||
                workforceDialog?.row?.project_code ||
                "-"}
            </SoftBox>
          </SoftTypography>

          {workforceLoading ? (
            <SoftTypography variant="caption" color="text">
              Loading hide rules...
            </SoftTypography>
          ) : (
            <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1.5}>
              <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                  Branch
                </SoftTypography>
                <SoftSelect
                  size="small"
                  compactMenu
                  placeholder="Select branch"
                  options={workforceBranchOptions}
                  value={workforceBranch}
                  onChange={(option) => {
                    setWorkforceBranch(option);
                    setWorkforceSubBranch([]);
                  }}
                  isClearable={false}
                  isSearchable
                />
              </SoftBox>
              <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                  Sub Branches
                </SoftTypography>
                <SoftSelect
                  size="small"
                  compactMenu
                  placeholder="Select sub branch"
                  options={workforceSubBranchOptions}
                  value={workforceSubBranch}
                  onChange={(options) => setWorkforceSubBranch(options || [])}
                  isDisabled={!workforceBranch}
                  isMulti
                  closeMenuOnSelect={false}
                  isClearable={false}
                  isSearchable
                />
              </SoftBox>
            </SoftBox>
          )}

          {!workforceLoading && workforceDialog?.automatic && (
            <SoftTypography variant="caption" color="success" display="block" mt={1}>
              This project is visible by default unless a branch and sub branch is hidden.
            </SoftTypography>
          )}
        </DialogContent>
        <DialogActions>
          <SoftButton
            color="secondary"
            variant="outlined"
            disabled={workforceSaving}
            onClick={() => setWorkforceDialog(null)}
          >
            Close
          </SoftButton>
          {Number(workforceDialog?.hideRuleCount || 0) > 0 && (
            <SoftButton
              color="secondary"
              variant="outlined"
              disabled={workforceLoading || workforceSaving}
              onClick={clearWorkforceAssignment}
            >
              Clear Hide Rules
            </SoftButton>
          )}
          <SoftButton
            color="info"
            disabled={
              workforceLoading ||
              workforceSaving ||
              !workforceBranch ||
              !(
                Array.isArray(workforceSubBranch)
                  ? workforceSubBranch.length
                  : workforceSubBranch
              )
            }
            onClick={saveWorkforceAssignment}
          >
            {workforceSaving ? "Saving..." : "Save Hide Rule"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Vendor Projects"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((current) => ({ ...current, open: false }))}
        time={3000}
      />
      <Footer />
    </DashboardLayout>
  );
}

export default VendorProjects;
